import * as React from 'react';
import { Text, UnorderedList, ListItem } from '@chakra-ui/react';

import {
  UploadButtonSection,
  UploadActions,
  UploadExample,
  ModalColumns,
  UploadComplete as UploadCompleteSection,
  AcknowledgeBox,
  RequirementsSummary,
  RequirementsRemedy
} from 'shared-components';
import placeholderExample from '../../../../../assets/images/sample-translated-bank-statement.jpg';
import { FamilySponsorType } from '../../../../../types';
import { bankStatementTransName, bankStatementName } from '../../../../../utilities/documents';

interface IProps {
  sponsorType: FamilySponsorType;
  complete: boolean;
  fileNames: string[];
  onClose: () => void;
  onContinue: () => void;
  onUpload: () => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
}

const TranslationModal: React.FC<IProps> = (props: IProps) => {
  const { infoOnly, docStatus, docKey, sponsorType } = props;

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  const transName = bankStatementTransName(sponsorType);
  const statementName = bankStatementName(sponsorType);

  return (
    <ModalColumns
      id={'bankTranslation'}
      title={transName}
      onClose={handleOnClose}
      supportingColumn={<UploadExample altText="Translated bank statement example" url={placeholderExample} />}
    >
      {infoOnly ? (
        <>
          <Text>
            During your visa application you may be required to provide an English translation of your{' '}
            {statementName.toLowerCase()}
          </Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            label="Return to document upload"
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
          />
        </>
      ) : (
        <>
          {props.complete ? (
            <UploadCompleteSection fileNames={props.fileNames} onUpload={props.onUpload} onContinue={props.onClose} />
          ) : (
            <>
              <Text>
                We require an <b>English translation of your {statementName.toLowerCase()}</b>.
              </Text>

              <RequirementsSummary
                title={<>Please ensure your {transName.toLowerCase()} meets the following requirements:</>}
              >
                <UnorderedList mb={4}>
                  <ListItem>
                    Any recommended changes to the {statementName.toLowerCase()} have been made prior to the translation
                    taking place
                  </ListItem>
                  <ListItem>The translation must be signed and dated by a qualified translator</ListItem>
                  <ListItem>
                    The translator must confirm the document is an accurate translation of the original
                  </ListItem>
                  <ListItem>Contact details and credentials for the translator must be included</ListItem>
                </UnorderedList>
              </RequirementsSummary>

              <RequirementsRemedy>
                <Text>Once you have a translation that meets the above requirements, please upload it below.</Text>
              </RequirementsRemedy>

              <UploadButtonSection onUpload={props.onUpload}>
                Awaiting upload of <b>{transName}</b>
              </UploadButtonSection>

              <UploadActions complete={props.complete} onContinue={props.onClose} />
            </>
          )}
        </>
      )}
    </ModalColumns>
  );
};

export default TranslationModal;
